import dayjs from 'dayjs'

/**
 * Formats a date string or Date object into relative time format
 * @param date - Date string (ISO format) or Date object
 * @returns Formatted relative time string
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const past = typeof date === 'string' ? new Date(date) : date

  // Calculate difference in seconds
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)

  // Handle invalid dates
  if (Number.isNaN(diffInSeconds)) {
    return 'Invalid date'
  }

  // Handle future dates
  if (diffInSeconds < 0) {
    return 'Just now'
  }

  // Just now (0-59 seconds)
  if (diffInSeconds < 60) {
    return 'Just now'
  }

  // Minutes (1-59 minutes)
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min`
  }

  // Hours (1-23 hours)
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} hr`
  }

  // Days
  const diffInDays = Math.floor(diffInHours / 24)

  // Yesterday
  if (diffInDays === 1) {
    return 'Yesterday'
  }

  // 2-6 days
  if (diffInDays < 7) {
    return `${diffInDays} day`
  }

  // Months (1-11 months) - check before weeks
  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths >= 1 && diffInMonths < 12) {
    return `${diffInMonths} mo`
  }

  // Weeks (1-4 weeks) - only if less than 1 month
  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 5) {
    return `${diffInWeeks} wk`
  }

  // Years (1+ years)
  const diffInYears = Math.floor(diffInDays / 365)
  return `${diffInYears} yr`
}

/**
 * Formats time in hh:mm AM/PM format
 */
function formatTime(date: Date | dayjs.Dayjs): string {
  const dayjsDate = dayjs(date)
  return dayjsDate.format('h:mm A')
}

/**
 * Formats a date string or Date object into comment thread timestamp format
 * @param date - Date string (ISO format) or Date object
 * @returns Formatted timestamp string
 *
 * Format rules:
 * - Within the same hour: "Just now" (0-59s) or "X min"
 * - Same day (today): "hh:mm AM/PM" (e.g., "4:35 PM")
 * - Within past week: "Day hh:mm AM/PM" (e.g., "Fri 4:35 PM")
 * - Older messages: "MMM DD, hh:mm AM/PM" (e.g., "Nov 25, 4:35 PM")
 */
export function formatCommentTime(date: string | Date): string {
  const now = dayjs()
  const past = dayjs(date)

  if (!past.isValid()) {
    return 'Invalid date'
  }

  const diffInSeconds = now.diff(past, 'second')

  if (diffInSeconds < 0) {
    return 'Just now'
  }

  // Within the same hour: show "Just now" (0-59s) or minutes
  if (diffInSeconds < 60) {
    return 'Just now'
  }

  const diffInMinutes = now.diff(past, 'minute')
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min`
  }

  // Check if it's today (same day)
  const isToday = now.isSame(past, 'day')

  if (isToday) {
    // Same-day messages: hh:mm AM/PM
    return formatTime(past)
  }

  // Check if within the past 7 days
  const diffInDays = now.diff(past, 'day')
  if (diffInDays < 7) {
    // Messages within the past week: Day hh:mm AM/PM
    return `${past.format('ddd')} ${formatTime(past)}`
  }

  // Older messages: MMM DD, hh:mm AM/PM
  return `${past.format('MMM D')}, ${formatTime(past)}`
}
